import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  FormControlLabel, InputLabel, MenuItem, Select, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import useOrganizationSearch from '../../hooks/useOrganizationSearch';
import { clientLedgerService } from '../../services/accounting/clientLedger.service';
import { defaultStatementRange, loadStatementPrefs, saveStatementPrefs } from './statementPrefs';

const FS = '0.8125rem';
const fieldSx = { '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: FS }, '& .MuiInputLabel-root': { fontSize: FS } };

/**
 * Generate Customer Statement (CUSTOMER-STATEMENT.md §7).
 * Props: open, onClose, typingCenterId, initialOrg ({ id, name } | null), initialRange ({ from, to } | null)
 */
const StatementDialog = ({ open, onClose, typingCenterId, initialOrg = null, initialRange = null }) => {
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [range, setRange] = useState(defaultStatementRange());
  const [prefs, setPrefs] = useState(loadStatementPrefs());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const { options, loading, onInputChange, filterOptions } = useOrganizationSearch({
    typingCenterId, enabled: open, mapOption: (o) => ({ id: o.id, name: o.name || o.organization_name }),
  });

  useEffect(() => {
    if (!open) return;
    setOrg(initialOrg);
    setRange(initialRange?.from && initialRange?.to ? initialRange : defaultStatementRange());
    setPrefs(loadStatementPrefs());
    setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const updatePrefs = (patch) => setPrefs((p) => {
    const next = { ...p, ...patch };
    saveStatementPrefs(next);
    return next;
  });

  const valid = org?.id && range.from && range.to && range.from <= range.to;

  const openStatement = () => {
    const q = new URLSearchParams({ from: range.from, to: range.to, view: prefs.view, lineage: prefs.lineage ? '1' : '0' });
    onClose();
    navigate(`/reports/client-ledger/${org.id}?${q.toString()}`);
  };

  const downloadPdf = async () => {
    setBusy(true);
    setError(null);
    try {
      const blob = await clientLedgerService.exportStatement(org.id, {
        format: 'pdf', ...range, view: prefs.view.toLowerCase(), lineage: prefs.lineage, remarkChars: prefs.remarkChars,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Statement-${(org.name || 'client').replace(/[^a-z0-9]+/gi, '-')}-${range.from}-${range.to}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      setError('Could not download the statement.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>Customer Statement</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2, fontSize: '0.75rem' }} onClose={() => setError(null)}>{error}</Alert>}
        <Autocomplete
          options={options}
          loading={loading}
          value={org}
          filterOptions={filterOptions}
          onInputChange={onInputChange}
          onChange={(e, v) => setOrg(v)}
          getOptionLabel={(o) => o?.name || ''}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          ListboxProps={{ sx: { '& .MuiAutocomplete-option': { fontSize: FS } } }}
          renderInput={(params) => <TextField {...params} size="small" label="Customer" required sx={fieldSx} autoFocus={!initialOrg} />}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 2 }}>
          <TextField type="date" size="small" label="From" value={range.from} InputLabelProps={{ shrink: true }} sx={fieldSx}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
          <TextField type="date" size="small" label="To" value={range.to} InputLabelProps={{ shrink: true }} sx={fieldSx}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
        </Box>

        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 2, mb: 0.5 }}>View</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup exclusive size="small" value={prefs.view} onChange={(e, v) => v && updatePrefs({ view: v })}
            sx={{ '& .MuiToggleButton-root': { height: 32, px: 1.5, fontSize: '0.75rem', textTransform: 'none' },
              '& .MuiToggleButton-root.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } } }}>
            <ToggleButton value="CUSTOMER">Customer</ToggleButton>
            <ToggleButton value="ACCOUNTING">Accounting</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
          {prefs.view === 'CUSTOMER'
            ? 'Invoices and the money the customer paid, with each invoice\'s status.'
            : 'Invoices against settled receipts, with the wallet balance shown separately.'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={prefs.lineage} disabled={prefs.view !== 'CUSTOMER'}
              onChange={(e) => updatePrefs({ lineage: e.target.checked })} />}
            label={<Typography sx={{ fontSize: FS }}>Show payment lineage</Typography>} />
          <FormControl size="small" sx={{ minWidth: 150, ...fieldSx }}>
            <InputLabel>Remarks length</InputLabel>
            <Select label="Remarks length" value={prefs.remarkChars} onChange={(e) => updatePrefs({ remarkChars: e.target.value })}>
              {[15, 30, 40].map((n) => <MenuItem key={n} value={n} sx={{ fontSize: FS }}>{n} characters</MenuItem>)}
              <MenuItem value={0} sx={{ fontSize: FS }}>Full text</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none', fontSize: FS }}>Cancel</Button>
        <Button onClick={downloadPdf} disabled={!valid || busy}
          sx={{ textTransform: 'none', fontSize: FS }}>
          {busy ? 'Downloading…' : 'Download PDF'}
        </Button>
        <Button variant="contained" onClick={openStatement} disabled={!valid || busy} sx={{ textTransform: 'none', fontSize: FS }}>
          Open Statement
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatementDialog;
